<!--
  Copyright (C) 2022 - Jeci SARL - https://jeci.fr

  This program is free software: you can redistribute it and/or modify
  it under the terms of the GNU Affero General Public License as
  published by the Free Software Foundation, either version 3 of the
  License, or (at your option) any later version.

  This program is distributed in the hope that it will be useful,
  but WITHOUT ANY WARRANTY; without even the implied warranty of
  MERCHANTABILITY or FITNESS FOR A PARTICULAR PURPOSE.  See the
  GNU Affero General Public License for more details.

  You should have received a copy of the GNU Affero General Public License
  along with this program.  If not, see https://www.gnu.org/licenses/agpl-3.0.html.
-->

<template>
  <div v-if="isSavedViewLoaded && !hideViewSelector" class="loaded-view-bar">
    <span class="loaded-view-label">
      <i class="ri-eye-line" />
      {{ $t("rechercheAvancee.activeView", { name: loadedView.name }) }}
    </span>
    <PrimeButton
      icon="ri-close-line"
      :label="$t('rechercheAvancee.closeView')"
      severity="danger"
      @click="$emit('close-view')"
      outlined
      size="small"
    />
  </div>
  <PrimeAccordion
    class="mt-4"
    :class="{ 'accordion-locked': hideViewSelector }"
    multiple
    :value="hideViewSelector ? ['1'] : ['0', '1']"
    :dt="{
      panel: { borderColor: 'transparent' },
      header: { borderColor: 'transparent' },
      content: { borderColor: 'transparent' },
    }"
  >
    <PrimeAccordionPanel v-if="!hideViewSelector" value="0">
      <PrimeAccordionHeader>{{
        $t("rechercheAvancee.savedViews")
      }}</PrimeAccordionHeader>
      <PrimeAccordionContent>
        <UserViewsFavoritesPanel
          ref="savedSearchesPanel"
          :global-only="globalOnly"
          @load-view="handleLoadView"
        />
      </PrimeAccordionContent>
    </PrimeAccordionPanel>
    <PrimeAccordionPanel value="1">
      <PrimeAccordionHeader>{{
        $t("rechercheAvancee.queryBuilder")
      }}</PrimeAccordionHeader>
      <PrimeAccordionContent>
        <!-- Multi-location section -->
        <div class="mt-2 mb-4">
          <div class="flex align-items-center gap-1 mb-2">
            <span class="font-bold">{{
              $t("rechercheAvancee.locationsTitle")
            }}</span>
            <i
              v-tooltip.right="$t('rechercheAvancee.locationsHint')"
              class="ri-information-line text-gray-400 cursor-help"
            />
          </div>
          <div
            v-if="locations.length === 0"
            class="text-center text-gray-500 p-3 border-1 border-dashed border-round"
          >
            {{ $t("rechercheAvancee.noLocations") }}
          </div>
          <div v-else class="flex flex-column gap-2">
            <div
              v-for="(loc, index) in locations"
              :key="loc.id || index"
              class="flex align-items-center p-2 border-1 border-round"
              :class="
                loc.negation
                  ? 'border-red-200 bg-red-50'
                  : 'border-green-200 bg-green-50'
              "
            >
              <span
                class="text-xs font-bold px-2 py-1 border-round mr-2"
                :class="
                  loc.negation
                    ? 'bg-red-100 text-red-700'
                    : 'bg-green-100 text-green-700'
                "
              >
                {{
                  loc.negation
                    ? $t("rechercheAvancee.excludePrefix")
                    : $t("rechercheAvancee.includePrefix")
                }}
              </span>
              <span class="font-semibold mr-1">{{ loc.type }}</span>
              <span class="text-gray-700 flex-grow-1">{{
                loc.label || loc.value
              }}</span>
              <PrimeButton
                icon="ri-delete-bin-line"
                text
                rounded
                severity="danger"
                :aria-label="$t('rechercheAvancee.removeLocation')"
                @click="removeLocationAtIndex(index)"
              />
            </div>
          </div>
          <PrimeButton
            icon="ri-add-line"
            :label="$t('rechercheAvancee.addLocationButton')"
            @click="isAddLocationDialogVisible = true"
            class="mt-3 w-full"
            outlined
          />
        </div>

        <!-- Criteria section -->
        <div class="mt-4">
          <span class="font-bold">{{
            $t("rechercheAvancee.criteriaTitle")
          }}</span>

          <div
            v-if="criteriaGroups.groups.length === 0"
            class="text-center text-gray-500 p-3 border-1 border-dashed border-round mt-2"
          >
            {{ $t("rechercheAvancee.noCriteria") }}
          </div>

          <div v-else class="flex flex-column mt-2">
            <template
              v-for="(group, groupIndex) in criteriaGroups.groups"
              :key="group.id"
            >
              <!-- OR separator between groups (fixed: groups are always joined by OR) -->
              <div v-if="groupIndex > 0" class="criteria-group-separator">
                <span class="criteria-group-separator-line" />
                <span class="criteria-group-separator-label">{{
                  $t("rechercheAvancee.operatorOr")
                }}</span>
                <span class="criteria-group-separator-line" />
              </div>

              <!-- Group block -->
              <div
                class="criteria-group-block"
                :class="{
                  'criteria-group-block--multi':
                    criteriaGroups.groups.length > 1,
                }"
              >
                <!-- Group header: remove button (only when multiple groups) -->
                <div
                  v-if="criteriaGroups.groups.length > 1"
                  class="flex align-items-center justify-content-end mb-2"
                >
                  <PrimeButton
                    icon="ri-close-line"
                    text
                    rounded
                    size="small"
                    severity="danger"
                    v-tooltip.left="$t('rechercheAvancee.removeGroup')"
                    @click="removeGroup(groupIndex)"
                  />
                </div>

                <!-- Criteria rows -->
                <div class="flex flex-column">
                  <template
                    v-for="(criterion, cIndex) in group.criteria"
                    :key="criterion.id"
                  >
                    <!-- Inline AND label between criteria (fixed: criteria within a group are always joined by AND) -->
                    <div v-if="cIndex > 0" class="criteria-inline-operator">
                      <span class="criteria-inline-operator-label">{{
                        $t("rechercheAvancee.operatorAnd")
                      }}</span>
                    </div>

                    <!-- Criterion row -->
                    <div
                      class="flex align-items-center p-2 border-1 border-round"
                      style="border-color: var(--p-content-border-color)"
                    >
                      <div class="flex-grow-1 flex flex-wrap gap-1">
                        <span class="font-bold">{{
                          $t(criterion.property.labelId)
                        }}</span>
                        <span
                          :class="
                            criterion.negation ? 'text-red-500' : 'text-primary'
                          "
                          >{{
                            criterion.negation
                              ? $t("rechercheAvancee.mustNotContain")
                              : $t("rechercheAvancee.mustContain")
                          }}</span
                        >
                        <span class="font-italic">{{
                          $t(criterion.operatorLabelKey)
                        }}</span>
                        <span class="text-gray-700">{{
                          formatCriterionValueForDisplay(criterion)
                        }}</span>
                      </div>
                      <PrimeButton
                        icon="ri-pencil-line"
                        text
                        rounded
                        severity="info"
                        @click="
                          startEditCriterion(groupIndex, cIndex, criterion)
                        "
                      />
                      <PrimeButton
                        icon="ri-delete-bin-line"
                        :aria-label="$t('delete')"
                        text
                        rounded
                        severity="danger"
                        @click="removeCriterionFromGroup(groupIndex, cIndex)"
                      />
                    </div>
                  </template>
                </div>

                <!-- Add criterion to this group -->
                <PrimeButton
                  icon="ri-add-line"
                  :label="$t('rechercheAvancee.addCriterionButton')"
                  @click="openAddCriterionForGroup(groupIndex)"
                  class="mt-2 w-full"
                  outlined
                  size="small"
                />
              </div>
            </template>
          </div>

          <!-- Action buttons -->
          <div class="flex gap-2 mt-3">
            <PrimeButton
              v-if="criteriaGroups.groups.length === 0"
              icon="ri-add-line"
              :label="$t('rechercheAvancee.addCriterionButton')"
              @click="handleAddFirstCriterion"
              class="flex-1"
              outlined
            />
            <PrimeButton
              v-if="criteriaGroups.groups.length > 0"
              icon="ri-add-line"
              :label="$t('rechercheAvancee.addOrGroup')"
              @click="handleAddGroup"
              class="flex-1"
              outlined
              size="small"
            />
          </div>

          <!-- View actions separator -->
          <div v-if="hasActiveCriteria" class="view-actions-separator" />

          <!-- Save as new view -->
          <PrimeButton
            v-if="hasActiveCriteria"
            icon="ri-save-3-line"
            :label="$t('rechercheAvancee.saveViewAsNew')"
            @click="openSaveViewPopup"
            class="w-full"
            outlined
          />

          <!-- Actions on loaded view -->
          <template v-if="canManageLoadedView">
            <PrimeButton
              icon="ri-save-line"
              :label="$t('rechercheAvancee.saveViewModifications')"
              @click="modifyView"
              class="mt-2 w-full"
            />
            <a class="delete-view-link" @click="deleteView">
              <i class="ri-delete-bin-line" />
              {{ $t("rechercheAvancee.deleteView") }}
            </a>
          </template>
        </div>
      </PrimeAccordionContent>
    </PrimeAccordionPanel>
  </PrimeAccordion>

  <!-- Add Location Dialog -->
  <PrimeDialog
    v-model:visible="isAddLocationDialogVisible"
    :header="$t('rechercheAvancee.addLocationTitle')"
    modal
    :style="{ width: '500px', maxWidth: '95vw' }"
  >
    <div class="flex flex-column gap-3">
      <div class="flex flex-column gap-1 w-full">
        <label class="font-semibold">{{
          $t("rechercheAvancee.conditionLabel")
        }}</label>
        <PrimeSelectButton
          v-model="newLocation.negation"
          :options="conditionOptions"
          optionLabel="label"
          optionValue="value"
          class="w-full"
        />
      </div>
      <div class="flex flex-column gap-1 w-full">
        <label class="font-semibold">{{
          $t("rechercheAvancee.locationTypeLabel")
        }}</label>
        <PrimeSelectButton
          v-model="newLocation.type"
          :options="locationOptions"
          optionLabel="label"
          optionValue="value"
          class="w-full"
        />
      </div>
      <div class="flex flex-column gap-1 w-full" v-if="newLocation.type">
        <label class="font-semibold">{{
          $t("rechercheAvancee.valueLabel")
        }}</label>
        <PrimeAutoComplete
          v-if="newLocation.type === 'Site'"
          v-model="newLocationSiteInput"
          :suggestions="filteredSites"
          optionLabel="title"
          @complete="searchSites"
          @item-select="handleNewLocationSiteSelect"
          :placeholder="$t('rechercheAvancee.sitePlaceholder')"
          class="w-full"
          forceSelection
        />
        <PrimeInputText
          v-else
          v-model="newLocation.value"
          :placeholder="$t('rechercheAvancee.folderPlaceholder')"
          @click="
            newLocation.type === 'Folder' ? (isDossierTreeVisible = true) : null
          "
          readonly
          class="w-full cursor-pointer"
        />
      </div>
    </div>
    <template #footer>
      <PrimeButton
        :label="$t('cancel')"
        icon="ri-close-line"
        @click="isAddLocationDialogVisible = false"
        text
      />
      <PrimeButton
        :label="$t('rechercheAvancee.addLocationConfirm')"
        icon="ri-check-line"
        @click="confirmAddLocation"
        :disabled="!newLocation.type || !newLocation.value"
      />
    </template>
  </PrimeDialog>

  <AddCriterionPopup
    v-model:visible="isAddCriterionDialogVisible"
    :properties="availableProperties"
    :edit-criterion="editingCriterion"
    @add-criterion="handleCriterionAdded"
    @update-criterion="handleCriterionUpdated"
  />

  <SaveViewPopup
    v-model:visible="saveViewDialogVisible"
    :view-data="viewToEdit"
    :error-message="saveViewError"
    :global-only="globalOnly"
    @save-view="handleSaveView"
    @clear-error="clearSaveViewError"
  />

  <PrimeDialog
    v-model:visible="isDossierTreeVisible"
    :header="$t('selectFolder')"
    modal
    class="w-full md:w-6"
  >
    <TreeExplorer
      :current-node="null"
      :showUserHome="showUserHome"
      @select-node="handleDossierSelected"
    />
  </PrimeDialog>

  <PrimeDialog
    v-model:visible="isDeleteConfirmVisible"
    :header="$t('rechercheAvancee.confirmDeleteTitle')"
    modal
    class="dialog-confirm-delete"
  >
    <div class="flex align-items-center">
      <i class="ri-alert-line mr-3 text-4xl" aria-hidden="true" />
      <span v-if="loadedView">{{
        $t("rechercheAvancee.confirmDelete", { name: loadedView.name })
      }}</span>
    </div>
    <template #footer>
      <PrimeButton
        :label="$t('no')"
        icon="ri-close-line"
        @click="isDeleteConfirmVisible = false"
        class="p-button-text"
      />
      <PrimeButton
        :label="$t('yes')"
        icon="ri-check-line"
        @click="handleDeleteConfirmation"
        class="p-button-danger"
        autofocus
      />
    </template>
  </PrimeDialog>
</template>
<script>
import { mapState, mapActions } from "pinia";
import { useUserViewStore } from "@/stores/userView.js";
import { useConfigStore } from "@/stores/config";
import { useUserStore } from "@/stores/user";
import TreeExplorer from "@/components/TreeExplorer.vue";
import workspaceService from "@/services/WorkspaceService.js";
import viewConfigService from "@/services/ViewConfigService.js";

import Accordion from "primevue/accordion";
import AccordionPanel from "primevue/accordionpanel";
import AccordionHeader from "primevue/accordionheader";
import AccordionContent from "primevue/accordioncontent";
import UserViewsFavoritesPanel from "./UserViewsFavoritesPanel.vue";
import SelectButton from "primevue/selectbutton";
import InputText from "primevue/inputtext";
import AutoComplete from "primevue/autocomplete";
import Button from "primevue/button";
import AddCriterionPopup from "@/components/search/AddCriterionPopup.vue";
import SaveViewPopup from "@/components/search/SaveViewPopup.vue";
import Dialog from "primevue/dialog";

export default {
  name: "AdvancedViewPanel",
  components: {
    PrimeAccordion: Accordion,
    PrimeAccordionPanel: AccordionPanel,
    PrimeAccordionHeader: AccordionHeader,
    PrimeAccordionContent: AccordionContent,
    UserViewsFavoritesPanel,
    PrimeSelectButton: SelectButton,
    PrimeInputText: InputText,
    PrimeAutoComplete: AutoComplete,
    PrimeButton: Button,
    AddCriterionPopup,
    SaveViewPopup,
    PrimeDialog: Dialog,
    TreeExplorer,
  },
  props: {
    globalOnly: {
      type: Boolean,
      default: false,
    },
    showUserHome: {
      type: Boolean,
      default: true,
    },
    /**
     * When true, the "Saved views" accordion panel is hidden.
     * Useful when a separate view list page handles view selection.
     */
    hideViewSelector: {
      type: Boolean,
      default: false,
    },
    /**
     * When true, bypasses the isPristyAdmin check for editing loaded views.
     * Useful for admin pages where the user already has edit permissions.
     */
    canEditLoadedView: {
      type: Boolean,
      default: false,
    },
  },
  emits: [
    "change-exact-search-settings",
    "load-view",
    "close-view",
    "view-saved",
  ],
  data() {
    return {
      isAddCriterionDialogVisible: false,
      isAddLocationDialogVisible: false,
      saveViewDialogVisible: false,
      saveViewDialogMode: "create",
      saveViewError: null,
      isDossierTreeVisible: false,
      isDeleteConfirmVisible: false,
      availableSites: [],
      filteredSites: [],
      newLocation: {
        type: null,
        value: "",
        negation: false,
        label: "",
      },
      newLocationSiteInput: null,
      editingCriterion: null,
      editingGroupIndex: null,
      editingCriterionIndex: null,
      targetGroupIndex: null,
    };
  },
  watch: {
    isAddCriterionDialogVisible(newVal) {
      if (!newVal) {
        this.editingCriterion = null;
        this.editingGroupIndex = null;
        this.editingCriterionIndex = null;
        this.targetGroupIndex = null;
      }
    },
  },
  async mounted() {
    this.checkPristyAdminStatus();
    await this.loadSites();
  },
  computed: {
    ...mapState(useUserStore, ["isPristyAdmin"]),
    ...mapState(useUserViewStore, [
      "locations",
      "criteriaGroups",
      "allCriteria",
      "loadedView",
    ]),
    locationOptions() {
      return [
        { label: this.$t("rechercheAvancee.locationSite"), value: "Site" },
        { label: this.$t("folder"), value: "Folder" },
      ];
    },
    conditionOptions() {
      return [
        { label: this.$t("rechercheAvancee.includePrefix"), value: false },
        { label: this.$t("rechercheAvancee.excludePrefix"), value: true },
      ];
    },
    hasActiveCriteria() {
      return !!(
        (this.allCriteria && this.allCriteria.length > 0) ||
        this.locations.length > 0
      );
    },
    isSavedViewLoaded() {
      return this.loadedView !== null;
    },
    canManageLoadedView() {
      if (!this.isSavedViewLoaded) {
        return false;
      }
      if (this.canEditLoadedView) {
        return true;
      }
      if (this.loadedView.scope === "user") {
        return true;
      }
      if (this.loadedView.scope === "global") {
        return this.isPristyAdmin;
      }
      return false;
    },
    viewToEdit() {
      return this.saveViewDialogMode === "edit" ? this.loadedView : null;
    },
    availableProperties() {
      const configStore = useConfigStore();
      return configStore.METADATA.filter(
        (meta) => meta.display && meta.display.includes("view"),
      );
    },
  },
  methods: {
    ...mapActions(useUserStore, ["checkPristyAdminStatus"]),
    ...mapActions(useUserViewStore, [
      "addLocation",
      "removeLocation",
      "addGroup",
      "removeGroup",
      "addCriterionToGroup",
      "removeCriterionFromGroup",
      "updateCriterionInGroup",
      "saveCurrentView",
      "updateCurrentView",
      "deleteCurrentView",
    ]),
    removeLocationAtIndex(index) {
      this.removeLocation(index);
    },
    resetNewLocation() {
      this.newLocation = {
        type: null,
        value: "",
        negation: false,
        label: "",
      };
      this.newLocationSiteInput = null;
    },
    confirmAddLocation() {
      if (!this.newLocation.type || !this.newLocation.value) {
        return;
      }
      this.addLocation({ ...this.newLocation });
      this.isAddLocationDialogVisible = false;
      this.resetNewLocation();
    },
    handleNewLocationSiteSelect(event) {
      const selectedSite = event.value;
      if (selectedSite && selectedSite.id) {
        this.newLocation.value = selectedSite.id;
        this.newLocation.label = selectedSite.title;
      }
    },
    clearSaveViewError() {
      this.saveViewError = null;
    },
    handleDossierSelected(node) {
      if (this.isAddLocationDialogVisible) {
        // Coming from the add-location dialog
        const nodeRef = node.id.startsWith("workspace://")
          ? node.id
          : `workspace://SpacesStore/${node.id}`;
        this.newLocation.value = nodeRef;
        this.newLocation.label = node.name || nodeRef;
      }
      this.isDossierTreeVisible = false;
    },
    openSaveViewPopup() {
      this.saveViewDialogMode = "create";
      this.saveViewDialogVisible = true;
    },
    modifyView() {
      this.saveViewDialogMode = "edit";
      this.saveViewDialogVisible = true;
    },
    deleteView() {
      this.isDeleteConfirmVisible = true;
    },
    async handleDeleteConfirmation() {
      this.isDeleteConfirmVisible = false;
      try {
        await this.deleteCurrentView();
        this.$toast.add({
          severity: "success",
          summary: this.$t("success"),
          detail: this.$t("rechercheAvancee.deleteViewSuccess"),
          life: 3000,
        });
        this.$refs.savedSearchesPanel.fetchSavedViews();
      } catch (error) {
        this.$toast.add({
          severity: "error",
          summary: this.$t("error.error"),
          detail: error.message,
          life: 5000,
        });
      }
    },
    async handleSaveView(payload) {
      this.clearSaveViewError();

      const config = {
        locations: JSON.parse(JSON.stringify(this.locations)),
        criteriaGroups: JSON.parse(JSON.stringify(this.criteriaGroups)),
      };

      // Global views → save as vue-*.json config file
      if (payload.scope === "global") {
        try {
          const content = {
            id: payload.filename.replace(/^vue-/, "").replace(/\.json$/, ""),
            name: payload.name,
            description: payload.description,
            config,
          };
          await viewConfigService.saveView(payload.filename, content);
          this.$toast.add({
            severity: "success",
            summary: this.$t("success"),
            detail: this.$t("rechercheAvancee.addsSavedViewSuccess"),
            life: 3000,
          });
          if (this.$refs.savedSearchesPanel) {
            this.$refs.savedSearchesPanel.fetchSavedViews();
          }
          this.saveViewDialogVisible = false;
          this.$emit("view-saved", {
            filename: payload.filename,
            name: payload.name,
            description: payload.description,
          });
        } catch (error) {
          this.$toast.add({
            severity: "error",
            summary: this.$t("error.error"),
            detail: error.message,
            life: 5000,
          });
        }
        return;
      }

      // Personal views → save in user's savedViews.json
      const isCreateMode = this.saveViewDialogMode === "create";
      let action;
      let searchData;
      let successMessageKey;

      if (isCreateMode) {
        action = this.saveCurrentView;
        successMessageKey = "rechercheAvancee.addsSavedViewSuccess";
        searchData = {
          id: crypto.randomUUID(),
          name: payload.name,
          description: payload.description,
          scope: payload.scope,
          config,
        };
      } else {
        action = this.updateCurrentView;
        successMessageKey = "rechercheAvancee.modifySavedViewSuccess";
        searchData = {
          ...this.loadedView,
          name: payload.name,
          description: payload.description,
          config,
        };
      }

      try {
        await action(searchData);
        this.$toast.add({
          severity: "success",
          summary: this.$t("success"),
          detail: this.$t(successMessageKey),
          life: 3000,
        });
        this.$refs.savedSearchesPanel.fetchSavedViews();
        this.saveViewDialogVisible = false;
      } catch (error) {
        this.$toast.add({
          severity: "error",
          summary: this.$t("error.error"),
          detail: error.message,
          life: 5000,
        });
      }
    },
    handleLoadView(searchConfig) {
      this.$emit("load-view", searchConfig);
    },
    handleAddFirstCriterion() {
      // Auto-create first group and open criterion popup
      this.addGroup("AND");
      this.targetGroupIndex = 0;
      this.editingCriterion = null;
      this.isAddCriterionDialogVisible = true;
    },
    handleAddGroup() {
      this.addGroup("AND");
    },
    openAddCriterionForGroup(groupIndex) {
      this.targetGroupIndex = groupIndex;
      this.editingCriterion = null;
      this.isAddCriterionDialogVisible = true;
    },
    handleCriterionAdded(criterion) {
      // If no groups exist, auto-create one
      if (this.criteriaGroups.groups.length === 0) {
        this.addGroup("AND");
        this.targetGroupIndex = 0;
      }
      const idx =
        this.targetGroupIndex !== null
          ? this.targetGroupIndex
          : this.criteriaGroups.groups.length - 1;
      this.addCriterionToGroup(idx, criterion);
      this.targetGroupIndex = null;
    },
    startEditCriterion(groupIndex, criterionIndex, criterion) {
      this.editingGroupIndex = groupIndex;
      this.editingCriterionIndex = criterionIndex;
      this.editingCriterion = JSON.parse(JSON.stringify(criterion));
      this.isAddCriterionDialogVisible = true;
    },
    handleCriterionUpdated(updated) {
      this.updateCriterionInGroup(
        this.editingGroupIndex,
        this.editingCriterionIndex,
        updated,
      );
      this.editingCriterion = null;
      this.editingGroupIndex = null;
      this.editingCriterionIndex = null;
    },
    formatCriterionValueForDisplay(criterion) {
      if (criterion.value === null || criterion.value === undefined) {
        return "";
      }
      if (criterion.operator === "range") {
        const start =
          criterion.value instanceof Date
            ? new Date(criterion.value).toLocaleDateString()
            : criterion.value || "*";
        const end =
          criterion.value_end instanceof Date
            ? new Date(criterion.value_end).toLocaleDateString()
            : criterion.value_end || "*";
        return `[${start} -> ${end}]`;
      } else if (criterion.value instanceof Date) {
        return new Date(criterion.value).toLocaleDateString();
      } else if (typeof criterion.value === "boolean") {
        return this.$t(criterion.value ? "true" : "false");
      } else {
        return `"${criterion.value}"`;
      }
    },
    async loadSites() {
      try {
        const sites = await workspaceService.getWorkspaces();
        this.availableSites = sites.map((site) => ({
          id: site.id,
          title: site.title,
          description: site.description,
        }));
      } catch (error) {
        console.error("Error loading sites:", error);
      }
    },
    searchSites(event) {
      const query = event.query.toLowerCase();
      if (!query) {
        this.filteredSites = [...this.availableSites];
      } else {
        this.filteredSites = this.availableSites.filter(
          (site) =>
            site.title.toLowerCase().includes(query) ||
            (site.description &&
              site.description.toLowerCase().includes(query)),
        );
      }
    },
  },
};
</script>
<!-- Non-scoped: Dialog teleported to body -->
<style>
.accordion-locked .p-accordionheader {
  display: none;
}

.accordion-locked .p-accordionpanel {
  border: none;
}

.accordion-locked .p-accordioncontent-content {
  padding: 0;
}

.dialog-confirm-delete {
  width: 350px;
}

.loaded-view-bar {
  display: flex;
  align-items: center;
  gap: 0.5rem;
  padding: 0.5rem 0.75rem;
  border-radius: 0.5rem;
  background: var(--p-blue-50);
  border: 1px solid var(--p-blue-200);
  margin-bottom: 0.5rem;
}

.loaded-view-label {
  display: flex;
  align-items: center;
  gap: 0.375rem;
  flex: 1;
  font-weight: 600;
  color: var(--p-blue-700);
  font-size: 0.875rem;
  min-width: 0;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}

.view-actions-separator {
  border-top: 1px solid var(--p-content-border-color);
  margin: 1rem 0 0.75rem;
}

.delete-view-link {
  display: inline-flex;
  align-items: center;
  gap: 0.25rem;
  margin-top: 0.5rem;
  font-size: 0.8rem;
  color: var(--p-text-muted-color);
  cursor: pointer;
  text-decoration: none;
}

.delete-view-link:hover {
  color: var(--p-red-500);
  text-decoration: underline;
}

/* Criteria groups styles */
.criteria-group-block--multi {
  border: 1px solid var(--p-content-border-color);
  border-radius: 0.5rem;
  padding: 0.75rem;
  background: var(--p-content-background);
}

.criteria-group-separator {
  display: flex;
  align-items: center;
  gap: 0.5rem;
  margin: 0.5rem 0;
}

.criteria-group-separator-line {
  flex: 1;
  height: 1px;
  background: var(--p-content-border-color);
}

.criteria-group-separator-label {
  font-size: 0.7rem;
  font-weight: 700;
  color: var(--p-text-muted-color);
  text-transform: uppercase;
  letter-spacing: 0.05em;
  padding: 0.15rem 0.5rem;
}

.criteria-inline-operator {
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 0.15rem 0;
}

.criteria-inline-operator-label {
  font-size: 0.65rem;
  font-weight: 700;
  color: var(--p-text-muted-color);
  text-transform: uppercase;
  letter-spacing: 0.05em;
}
</style>
